﻿import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Database, Layers, User as UserIcon, LayoutGrid, Gauge } from 'lucide-react';
import EvalPickerPane from 'shared/components/filters/evals/EvalPickerPane';
import usePropertyCatalog from 'shared/hooks/usePropertyCatalog';
import { primary as PRIMARY } from 'shared/theme/trodoColors';
import { activityFilterPopupTheme as fp } from 'shared/theme/activityFilterPopupTheme';
import { useMenuPlacement } from 'shared/components/ui/menuPlacement';

/* ------------------------------------------------------------------ */
/* Anchored fixed-position popup                                       */
/* ------------------------------------------------------------------ */
function useAnchoredPosition({ open, triggerRef, width, height }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  // Inside a dialog that asks for upward menus, open above whenever the whole popup fits there.
  const preferTop = useMenuPlacement() === 'top';
  useLayoutEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const trig = triggerRef?.current?.getBoundingClientRect?.();
      if (!trig) return;
      const margin = 12;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const wantedTop = trig.bottom + 8;
      const flipTop = trig.top - height - 8;
      const top = preferTop
        ? (flipTop >= margin ? flipTop : wantedTop)
        : ((wantedTop + height + margin > vh && flipTop > margin) ? flipTop : wantedTop);
      let left = trig.left;
      if (left + width + margin > vw) left = Math.max(margin, vw - width - margin);
      setPos({ top: Math.max(margin, top), left: Math.max(margin, left) });
    };
    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, triggerRef, width, height, preferTop]);
  return pos;
}

/* ------------------------------------------------------------------ */
/* Groups: fixed ones are static, the rest load a page at a time        */
/* ------------------------------------------------------------------ */
/**
 * Fixed groups come from the core catalog (no discovery cost). Deferred groups
 * (`server`) are the keys a team's own data introduced — run attributes, run
 * metadata, span attributes, custom user properties — and are fetched page by
 * page, never up front.
 */
const GROUP_DEFS = [
  { id: 'run.fixed',    label: 'Run properties',           tabId: 'run',  category: 'agent_properties', core: 'run_properties' },
  { id: 'span.fixed',   label: 'Span properties',          tabId: 'span', category: 'span_properties',  core: 'span_properties' },
  { id: 'user.fixed',   label: 'User properties',          tabId: 'user', category: 'user_properties',  core: 'user_properties' },
  { id: 'run.attrs',    label: 'Run attributes (custom)',  tabId: 'run',  category: 'agent_properties', server: 'run_attributes' },
  { id: 'run.metadata', label: 'Run metadata',             tabId: 'run',  category: 'agent_properties', server: 'run_metadata' },
  { id: 'span.attrs',   label: 'Span attributes (custom)', tabId: 'span', category: 'span_properties',  server: 'span_attributes' },
  { id: 'user.custom',  label: 'User attributes (custom)', tabId: 'user', category: 'user_properties',  server: 'user_custom' },
];

/** How a server property becomes a pickable item. */
function toItem(def, p) {
  const base = { key: p.name, label: p.name, dataType: p.type, sampleValues: p.sampleValues || [] };
  switch (def.id) {
    case 'run.fixed':    return { ...base, field: p.name, source: 'fixed' };
    case 'run.attrs':    return { ...base, field: `attributes.${p.name}`, source: 'attributes' };
    case 'run.metadata': return { ...base, field: `metadata.${p.name}`, source: 'metadata' };
    case 'span.fixed':   return { ...base, field: `span.${p.name}`, source: 'span_fixed' };
    case 'span.attrs':   return { ...base, field: `span.attributes.${p.name}`, source: 'span_attributes' };
    default:             return { ...base, field: `user.${p.name}`, source: 'user' };
  }
}

/**
 * Which groups and tabs this picker offers.
 * On a spans surface the run groups are not merely irrelevant, they are a trap:
 * the fields they emit (total_cost, span_count, parent_run_id) are not in the
 * server's SPAN_FIXED_TYPES whitelist, so the compiler drops them and you get a
 * chip that looks active and filters nothing. Span groups are emitted
 * `span.`-prefixed and the span compiler strips that, so those work.
 */
function groupsFor(scope, entity) {
  const spanOnly = entity === 'span';
  const defs = GROUP_DEFS.filter((d) => {
    if (spanOnly && d.tabId === 'run') return false;
    if (scope !== 'global' && d.tabId === 'user') return false;
    return true;
  });
  const tabs = [{ id: 'all', label: 'All', Icon: LayoutGrid }];
  if (!spanOnly) tabs.push({ id: 'run', label: 'Run', Icon: Database });
  tabs.push({ id: 'span', label: 'Span', Icon: Layers });
  if (scope === 'global') tabs.push({ id: 'user', label: 'User', Icon: UserIcon });
  return { defs, tabs };
}

const SCROLL_LOAD_THRESHOLD_PX = 80;

/* ------------------------------------------------------------------ */
/* Public component                                                    */
/* ------------------------------------------------------------------ */
/**
 * Property picker — pick one property and fire `onPick` with everything
 * the chip needs to render the right operator menu + value editor:
 *   { field, dataType, listItemType, source, category, sampleValues, agentName? }
 */
export default function PropertyPickerPopover({
  open,
  onClose,
  anchorRef,
  teamId,
  scope,           // 'agent' | 'global'
  agentName,       // when scope === 'agent'
  onPick,
  /**
   * Which entity is being filtered: 'run' (default) or 'span'.
   *
   * A runs surface can legitimately filter by span properties — "runs
   * containing a span using model X" — so 'run' offers both. A spans surface
   * cannot do the reverse: run fields like total_cost or span_count do not
   * exist on agent_spans, the server's SPAN_FIXED_TYPES whitelist drops them,
   * and the chip sits there looking active while filtering nothing. Passing
   * 'span' removes them from the catalog so they cannot be picked at all,
   * rather than offering a choice that quietly does not work.
   */
  entity = 'run',
  /**
   * With it, an Evals tab: an eval, then its verdict, score, reason code or
   * one step's answer. Called with one eval condition line; the builder shows
   * it as a chip in Filters. `only="evals"` shows that tab alone (a surface
   * with no properties to filter, like Conversations).
   */
  onPickEval = null,
  only = null,
}) {
  const scopedAgent = scope === 'agent' ? agentName : null;
  const { defs, tabs } = useMemo(() => {
    const g = only === 'evals' ? { defs: [], tabs: [] } : groupsFor(scope, entity);
    if (onPickEval) g.tabs = [...g.tabs, { id: 'evals', label: 'Evals', Icon: Gauge }];
    return g;
  }, [scope, entity, only, onPickEval]);
  const serverGroups = useMemo(() => defs.filter((d) => d.server).map((d) => d.server), [defs]);

  const { core, pages, search: serverSearch, loadNext, setQuery } = usePropertyCatalog({
    open, teamId, agentName: scopedAgent, groups: serverGroups,
  });

  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open) return;
    // 'all' is safe to land on now: when entity is 'span' it contains only
    // properties that actually compile.
    setActiveTab(only === 'evals' ? 'evals' : 'all');
    setSearch('');
    setQuery('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSearchChange = (value) => {
    setSearch(value);
    setQuery(value);
  };

  const searching = search.trim() !== '';

  /** Deferred groups on the visible tab that still have pages to fetch. */
  const loadableServerGroups = useMemo(
    () => defs
      .filter((d) => d.server && (activeTab === 'all' || d.tabId === activeTab))
      .map((d) => d.server)
      .filter((g) => pages[g] && pages[g].hasMore),
    [defs, activeTab, pages],
  );
  const anyLoading = defs.some((d) => d.server && pages[d.server]?.loading);
  const busy = search.trim() !== '' ? serverSearch.loading : anyLoading;

  const allGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byServer = {};
    serverSearch.items.forEach((p) => { (byServer[p.group] = byServer[p.group] || []).push(p); });
    return defs
      .filter((d) => activeTab === 'all' || d.tabId === activeTab)
      .map((d) => {
        let props;
        if (d.core) {
          props = core[d.core] || [];
        } else {
          const loaded = pages[d.server]?.items || [];
          // Names the server found that this group has not paged in yet.
          const seen = new Set(loaded.map((i) => i.name));
          const extra = q ? (byServer[d.server] || []).filter((p) => !seen.has(p.name)) : [];
          props = loaded.concat(extra);
        }
        let items = props.map((p) => toItem(d, p));
        if (q) items = items.filter((i) => i.label.toLowerCase().includes(q));
        return { ...d, items };
      })
      .filter((g) => g.items.length > 0);
  }, [defs, activeTab, core, pages, search, serverSearch.items]);
  const visibleGroups = allGroups;

  // Scroll to the end of the list, or a list too short to scroll: bring in the
  // next page of the deferred properties. Not while searching (the server
  // search answers that) and never more than one group at a time.
  const listRef = useRef(null);
  const maybeLoadNext = useCallback(() => {
    if (!open || searching || anyLoading || loadableServerGroups.length === 0) return;
    loadNext(loadableServerGroups);
  }, [open, searching, anyLoading, loadableServerGroups, loadNext]);
  const onListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_LOAD_THRESHOLD_PX) maybeLoadNext();
  };
  useEffect(() => {
    const el = listRef.current;
    // clientHeight is 0 until laid out; only act on a real, too-short list.
    if (el && el.clientHeight > 0 && el.scrollHeight <= el.clientHeight + 8) maybeLoadNext();
  }, [maybeLoadNext, visibleGroups.length, activeTab, open]);

  const handlePick = (group, item) => {
    const row = {
      field: item.field,
      dataType: item.dataType || 'string',
      listItemType: item.listItemType || null,
      source: item.source,
      sampleValues: item.sampleValues || [],
      category: scope === 'agent'
        ? group.category
        : (group.category === 'agent_properties' ? 'global' : group.category),
    };
    if (scope === 'agent' && agentName) row.agentName = agentName;
    onPick(row);
    onClose();
  };

  // Outside / Esc close.
  const popRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose();
    };
    // Escape closes this picker, not the dialog it sits in.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  const POP_W = typeof window !== 'undefined' ? Math.min(760, window.innerWidth - 16) : 760;
  const POP_H = typeof window !== 'undefined' ? Math.min(460, window.innerHeight - 32) : 460;
  const pos = useAnchoredPosition({ open, triggerRef: anchorRef, width: POP_W, height: POP_H });

  if (!open) return null;
  return createPortal(
    <div
      ref={popRef}
      className="fixed z-[9999] flex flex-col sm:flex-row overflow-hidden rounded-2xl border bg-[color:var(--surface-raised)] shadow-2xl"
      style={{
        top: pos.top, left: pos.left, width: POP_W, height: POP_H,
        borderColor: fp.border,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Left nav (becomes horizontal scrolling tabs on mobile) */}
      <div
        className="flex w-full sm:w-[180px] flex-shrink-0 flex-row sm:flex-col border-b sm:border-b-0 sm:border-r overflow-x-auto sm:overflow-x-visible"
        style={{ borderColor: fp.border, backgroundColor: fp.bgNav }}
      >
        <div className="flex flex-1 sm:flex-col flex-row gap-0.5 overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto p-1.5">
          {tabs.map((t) => {
            const Icon = t.Icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className="flex flex-shrink-0 sm:w-full w-auto items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors whitespace-nowrap hover:bg-surface-hover"
                style={{
                  backgroundColor: active ? fp.navActiveBg : undefined,
                  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontWeight: 500,
                }}
              >
                <Icon size={14} style={{ color: active ? PRIMARY : 'var(--text-disabled)' }} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right pane */}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b px-3 py-2" style={{ borderColor: fp.border }}>
          <Search size={14} className="text-[color:var(--text-muted)]" />
          <input
            autoFocus
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={activeTab === 'evals' ? 'Search evals and steps…' : `Search ${scope === 'agent' ? agentName + ' ' : ''}properties…`}
            className="flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div ref={listRef} onScroll={onListScroll} className="flex-1 overflow-y-auto">
          {activeTab === 'evals' ? (
            <EvalPickerPane teamId={teamId} search={search} onPick={(line) => { onPickEval(line); onClose(); }} />
          ) : visibleGroups.length === 0 ? (
            <div className="px-4 py-6 text-center text-[12px] text-[color:var(--text-muted)]">
              {busy ? 'Loading properties…' : 'No matching properties.'}
            </div>
          ) : (
            visibleGroups.map((g) => (
              <div key={g.id}>
                <div
                  className="px-3 pb-1 pt-3 text-[11px] font-medium"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {g.label}
                </div>
                {g.items.map((i) => (
                  <button
                    key={i.field}
                    type="button"
                    onClick={() => handlePick(g, i)}
                    className="mx-1.5 flex w-[calc(100%-12px)] items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors hover:bg-surface-hover"
                  >
                    <span className="font-mono text-[color:var(--text-primary)] truncate">{i.label}</span>
                    <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-[color:var(--text-muted)]">
                      {i.dataType || 'string'}
                    </span>
                  </button>
                ))}
              </div>
            ))
          )}
          {activeTab !== 'evals' && busy && visibleGroups.length > 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-[color:var(--text-muted)]">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
              {searching ? 'Searching…' : 'Loading more properties…'}
            </div>
          ) : null}
          {activeTab !== 'evals' && !busy && !searching && loadableServerGroups.length > 0 ? (
            <button
              type="button"
              onClick={() => loadNext(loadableServerGroups)}
              className="mx-1.5 my-1 w-[calc(100%-12px)] rounded-lg px-2.5 py-1.5 text-left text-[12px] text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            >
              Load more
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
